import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc, getDocs } from "firebase/firestore";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Seite from "../components/Seite";
import MikrofonButton from "../components/MikrofonButton";

function ProjektAuswahl({ user, onWaehlen }) {
  const [projekte, setProjekte] = useState([]);
  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", user.uid)));
      setProjekte(snap.docs.map((d) => ({ id: d.id, titel: d.data().titel })));
    })();
  }, [user.uid]);
  return (
    <select style={styles.select} onChange={(e) => e.target.value && onWaehlen(e.target.value)} defaultValue="">
      <option value="" disabled>Projekt wählen...</option>
      <option value="__neu__">+ Neues Projekt erstellen</option>
      {projekte.map((p) => <option key={p.id} value={p.id}>{p.titel}</option>)}
    </select>
  );
}

function SortableListe({ liste, onDelete, user }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: liste.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const [editTitel, setEditTitel] = useState(false);
  const [titelText, setTitelText] = useState(liste.titel);
  const [editKat, setEditKat] = useState(liste.kategorie || "privat");
  const [neuerPunkt, setNeuerPunkt] = useState("");
  const [editPunktId, setEditPunktId] = useState(null);
  const [editPunktText, setEditPunktText] = useState("");
  const [projektVerschiebenOffen, setProjektVerschiebenOffen] = useState(false);

  const punktHinzufuegen = async (e) => {
    e.preventDefault();
    if (!neuerPunkt.trim()) return;
    const punkte = [...(liste.punkte || []), { text: neuerPunkt, erledigt: false, id: Date.now().toString() }];
    await updateDoc(doc(db, "todo", liste.id), { punkte });
    setNeuerPunkt("");
  };

  const punktToggle = async (punkt) => {
    const punkte = (liste.punkte || []).map((p) => p.id === punkt.id ? { ...p, erledigt: !p.erledigt } : p);
    await updateDoc(doc(db, "todo", liste.id), { punkte });
  };

  const punktLoeschen = async (punkt) => {
    const punkte = (liste.punkte || []).filter((p) => p.id !== punkt.id);
    await updateDoc(doc(db, "todo", liste.id), { punkte });
  };

  const punktSpeichern = async (punkt) => {
    const punkte = (liste.punkte || []).map((p) => p.id === punkt.id ? { ...p, text: editPunktText } : p);
    await updateDoc(doc(db, "todo", liste.id), { punkte });
    setEditPunktId(null);
  };

  const titelSpeichern = async () => {
    await updateDoc(doc(db, "todo", liste.id), { titel: titelText, kategorie: editKat });
    setEditTitel(false);
  };

  const zuProjektVerschieben = async (projektId) => {
    const unterpunkte = (liste.punkte || []).map((p) => ({
      id: p.id || Date.now().toString() + Math.random(),
      text: p.text,
      erledigt: p.erledigt || false,
      prioritaet: "mittel",
      erledigtBis: "",
      notiz: {},
    }));
    if (projektId === "__neu__") {
      await addDoc(collection(db, "projekte"), {
        titel: liste.titel,
        unterpunkte,
        uid: liste.uid,
        reihenfolge: Date.now(),
        erstellt: new Date(),
      });
    } else {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", liste.uid)));
      const projektDoc = snap.docs.find((d) => d.id === projektId);
      if (projektDoc) {
        const bisherige = projektDoc.data().unterpunkte || [];
        await updateDoc(doc(db, "projekte", projektId), { unterpunkte: [...bisherige, ...unterpunkte] });
      }
    }
    await deleteDoc(doc(db, "todo", liste.id));
    setProjektVerschiebenOffen(false);
  };

  const kat = liste.kategorie || "privat";
  const katFarbe = kat === "beruflich" ? "#1a73e8" : "#34a853";
  const katLabel = kat === "beruflich" ? "Beruflich" : "Privat";

  return (
    <div ref={setNodeRef} style={{ ...styles.liste, ...style }}>
      <div style={styles.listeHeader}>
        <span style={styles.dragHandle} {...attributes} {...listeners}>⠿</span>
        {editTitel ? (
          <>
            <input style={{ ...styles.input, flex: 1 }} value={titelText} onChange={(e) => setTitelText(e.target.value)} autoFocus />
            <select style={styles.select} value={editKat} onChange={(e) => setEditKat(e.target.value)}>
              <option value="privat">Privat</option>
              <option value="beruflich">Beruflich</option>
            </select>
            <button style={styles.speichern} onClick={titelSpeichern}>💾</button>
            <button style={styles.abbrechen} onClick={() => setEditTitel(false)}>✕</button>
          </>
        ) : (
          <>
            <span style={styles.listeTitel}>{liste.titel}</span>
            <span style={{ ...styles.katBadge, backgroundColor: katFarbe }}>{katLabel}</span>
            <button style={styles.iconBtn} title="Zu Projekt verschieben" onClick={() => setProjektVerschiebenOffen(!projektVerschiebenOffen)}>📁</button>
            <button style={styles.iconBtn} onClick={() => { setEditTitel(true); setTitelText(liste.titel); setEditKat(kat); }}>✏️</button>
            <button style={styles.iconBtn} onClick={() => onDelete(liste.id)}>🗑️</button>
          </>
        )}
      </div>

      {projektVerschiebenOffen && (
        <div style={{ padding: "0.5rem 0", marginBottom: "0.5rem" }}>
          <ProjektAuswahl user={{ uid: liste.uid }} onWaehlen={zuProjektVerschieben} />
        </div>
      )}

      {(liste.punkte || []).map((punkt) => (
        <div key={punkt.id} style={styles.punkt}>
          <input type="checkbox" checked={punkt.erledigt || false} onChange={() => punktToggle(punkt)} />
          {editPunktId === punkt.id ? (
            <>
              <input style={{ ...styles.input, flex: 1 }} value={editPunktText} onChange={(e) => setEditPunktText(e.target.value)} autoFocus />
              <button style={styles.speichern} onClick={() => punktSpeichern(punkt)}>💾</button>
              <button style={styles.abbrechen} onClick={() => setEditPunktId(null)}>✕</button>
            </>
          ) : (
            <>
              <span style={{ flex: 1, textDecoration: punkt.erledigt ? "line-through" : "none", color: punkt.erledigt ? "#999" : "inherit" }}>{punkt.text}</span>
              <button style={styles.iconBtn} onClick={() => { setEditPunktId(punkt.id); setEditPunktText(punkt.text); }}>✏️</button>
              <button style={styles.iconBtn} onClick={() => punktLoeschen(punkt)}>🗑️</button>
            </>
          )}
        </div>
      ))}

      <form onSubmit={punktHinzufuegen} style={styles.punktForm}>
        <input style={styles.punktInput} value={neuerPunkt} onChange={(e) => setNeuerPunkt(e.target.value)} placeholder="Neuer Unterpunkt..." />
        <MikrofonButton onText={(t) => setNeuerPunkt((prev) => prev + (prev ? " " : "") + t)} />
        <button style={styles.kleinButton} type="submit">+</button>
      </form>
    </div>
  );
}

export default function Todo({ user }) {
  const [listen, setListen] = useState([]);
  const [neueTitel, setNeueTitel] = useState("");
  const [kategorie, setKategorie] = useState("privat");
  const [filter, setFilter] = useState("alle");
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const q = query(collection(db, "todo"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.reihenfolge || 0) - (b.reihenfolge || 0));
      setListen(data);
    });
  }, [user.uid]);

  const listeHinzufuegen = async (e) => {
    e.preventDefault();
    if (!neueTitel.trim()) return;
    await addDoc(collection(db, "todo"), { titel: neueTitel, kategorie, punkte: [], uid: user.uid, reihenfolge: listen.length, erstellt: new Date() });
    setNeueTitel("");
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = listen.findIndex((l) => l.id === active.id);
    const newIndex = listen.findIndex((l) => l.id === over.id);
    const neu = arrayMove(listen, oldIndex, newIndex);
    setListen(neu);
    for (let i = 0; i < neu.length; i++) {
      await updateDoc(doc(db, "todo", neu[i].id), { reihenfolge: i });
    }
  };

  const gefiltert = filter === "alle"
    ? listen
    : listen.filter((l) => (l.kategorie || "privat") === filter);

  return (
    <Seite titel="ToDo">
      <form onSubmit={listeHinzufuegen} style={styles.form}>
        <input style={styles.input} value={neueTitel} onChange={(e) => setNeueTitel(e.target.value)} placeholder="Neue Liste (Überschrift)..." />
        <MikrofonButton onText={(t) => setNeueTitel((prev) => prev + (prev ? " " : "") + t)} />
        <select style={styles.select} value={kategorie} onChange={(e) => setKategorie(e.target.value)}>
          <option value="privat">Privat</option>
          <option value="beruflich">Beruflich</option>
        </select>
        <button style={styles.button} type="submit">Erstellen</button>
      </form>

      <div style={styles.filterBar}>
        {[
          { key: "alle", label: "Alle" },
          { key: "privat", label: "🟢 Privat" },
          { key: "beruflich", label: "🔵 Beruflich" },
        ].map((f) => (
          <button key={f.key}
            style={{ ...styles.filterBtn, backgroundColor: filter === f.key ? "#1a3558" : "#dde3ec", color: filter === f.key ? "white" : "#333" }}
            onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={gefiltert.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {gefiltert.map((liste) => (
            <SortableListe key={liste.id} liste={liste} onDelete={(id) => deleteDoc(doc(db, "todo", id))} user={user} />
          ))}
        </SortableContext>
      </DndContext>
      {gefiltert.length === 0 && <p style={styles.leer}>Keine Listen vorhanden.</p>}
    </Seite>
  );
}

const styles = {
  form: { display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" },
  input: { flex: 1, padding: "0.6rem", fontSize: "1rem", border: "1px solid #ccc", borderRadius: "4px", minWidth: "120px" },
  select: { padding: "0.6rem", border: "1px solid #ccc", borderRadius: "4px", fontSize: "0.95rem" },
  button: { padding: "0.6rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  filterBar: { display: "flex", gap: "0.5rem", marginBottom: "1rem" },
  filterBtn: { padding: "0.4rem 0.8rem", border: "none", borderRadius: "20px", cursor: "pointer", fontSize: "0.9rem" },
  liste: { backgroundColor: "white", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  listeHeader: { display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" },
  dragHandle: { cursor: "grab", fontSize: "1.2rem", color: "#999", userSelect: "none" },
  listeTitel: { fontWeight: "bold", flex: 1 },
  katBadge: { fontSize: "0.7rem", color: "white", padding: "0.2rem 0.5rem", borderRadius: "10px" },
  punkt: { display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", borderBottom: "1px solid #f5f5f5" },
  punktForm: { display: "flex", gap: "0.4rem", marginTop: "0.6rem" },
  punktInput: { flex: 1, padding: "0.4rem", fontSize: "0.9rem", border: "1px solid #ccc", borderRadius: "4px" },
  kleinButton: { padding: "0.4rem 0.7rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  speichern: { padding: "0.3rem 0.6rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  abbrechen: { padding: "0.3rem 0.6rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  leer: { color: "#999", textAlign: "center" },
};
